import { Box, Grid, Stack } from "@mui/material";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import type { EventType } from "@/global/store/data/types";
import type { PacksList } from "@/types";
import { CombinedEventCard } from "@/shared/common/EventCard/CombinedEventCard";
import { isBucketMinMaxEventSelectionReached } from "@/shared/modules/PurchaseSeats/utils.events";
import { findEventsExceptAllPacksBonus } from "./packs.utils";
import { EmptyState } from "@/shared/base/components/EmptyState/EmptyState";

type BucketEventsContainerProps = {
  bucket: PacksList["buckets"][0];
  selectedEvents: EventType[];
  useFeaturedGames: boolean;
  onClickPreview?: (event: EventType) => void;
  onSelect: (event: EventType, bucketId: number) => void;
  onDeselect: (event: EventType, bucketId: number) => void;
};

export function BucketEventsContainer({
  bucket,
  useFeaturedGames,
  onClickPreview,
  onSelect,
  onDeselect,
  ...restProps
}: BucketEventsContainerProps) {
  const teamConfig = useTeamConfig();
  const events = findEventsExceptAllPacksBonus(bucket.events, teamConfig);
  const selectedEvents = findEventsExceptAllPacksBonus(
    restProps.selectedEvents,
    teamConfig
  );

  if (!events?.length) {
    return (
      <Stack p={4} alignItems={"center"} justifyContent={"center"}>
        <EmptyState
          title={"No events available"}
          description={"Try adjusting your filters."}
        />
      </Stack>
    );
  }

  return (
    <Grid container spacing={4}>
      {events?.map((evt, idx) => {
        const isSelected = selectedEvents.some(
          (ev) => ev.eventCode == evt.eventCode
        );
        const { isMaxEventCountReached } = isBucketMinMaxEventSelectionReached(
          bucket,
          selectedEvents,
          teamConfig
        );

        return (
          <Grid key={idx} item xs={12} md={6} lg={4} xl={3}>
            <CombinedEventCard
              variant={useFeaturedGames ? "featured" : "upcoming"}
              event={evt}
              isDisabled={isMaxEventCountReached && !isSelected}
              isSelected={isSelected}
              onClickPreview={() => onClickPreview?.(evt)}
              onSelect={() => {
                if (isSelected) return onDeselect(evt, bucket.id as number);
                onSelect(evt, bucket.id as number);
              }}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}
